import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import DeleteErrorContent from 'contents/error/DeleteErrorContent';
import FrontErrorContent from 'contents/error/FrontErrorContent';
import NoJWTErrorContent from 'contents/error/NoJWTErrorContent';
import ServerErrorContent from 'contents/error/ServerErrorContent';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      code: '0',
      message: '',
      details: '',
      errorType: '', // errorType을 추가하여 프론트 오류인지 서버 오류인지 구분
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorType: 'frontend' }; // 프론트 오류로 처리
  }

  componentDidMount() {
    // 비동기 에러 처리
    window.onerror = (message, source, lineno, colno, error) => {
      const errorObj = error || { message: message || '예기치 못한 오류가 발생했습니다.' };
      this.handleError(errorObj, 'frontend'); // 프론트 오류로 처리
    };

    window.onunhandledrejection = (event) => {
      this.handleError(event.reason, 'frontend'); // 프론트 오류로 처리
    };
  }

  componentWillUnmount() {
    window.onerror = null;
    window.onunhandledrejection = null;
  }

  resetError = () => {
    this.setState({
      hasError: false,
      code: '0',
      message: '',
      details: '',
      errorType: '',
    });
  };

  handleError = (error, errorType = 'backend') => {
    if (error && error.isAxiosError && error.response) {
      // 서버 오류 처리
      const { message, details } = error.response?.data?.data || {};

      this.setState({
        hasError: true,
        code: error?.response?.data?.data?.code,
        message: message || '서버 오류가 발생했습니다.',
        details: details || '',
        errorType: 'backend', // 서버 오류로 처리
      });
    } else {
      // 프론트 오류 처리
      const message = error?.message || (typeof error === 'string' ? error : '예기치 못한 오류가 발생했습니다.');
      this.setState({
        hasError: true,
        message: message,
        details: error ? JSON.stringify(error, null, 2) : '',
        errorType: 'frontend', // 프론트 오류로 처리
      });
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.state.errorType === 'frontend') {
        // 프론트 오류일 경우
        return <FrontErrorContent>{this.props.children}</FrontErrorContent>;
      } else if (this.state.errorType === 'backend') {
        // 서버 오류일 경우
        switch (this.state.code) {
          case '7801':
          case '7803':
          case '7804':
          case '7805':
          case '7806':
          case '7807':
          case '7000':
            return (
              <NoJWTErrorContent code={this.state.code}>
                {this.props.children}
              </NoJWTErrorContent>
            );
          // 삭제된 게시물일 경우
          case '0003':
          case '1000':
          case '3000':
            return (
              <DeleteErrorContent code={this.state.code}>
                {this.props.children}
              </DeleteErrorContent>
            );
          default:
            return (
              <ServerErrorContent>{this.props.children}</ServerErrorContent>
            );
        }
      }
    }
    // 에러가 발생하지 않은 경우 자식 컴포넌트를 렌더링
    return this.props.children;
  }
}

export default ErrorBoundary;

/**
 * 라우트 변경 시 ErrorBoundary의 에러 상태를 자동으로 리셋하는 래퍼 컴포넌트
 * PastLayout, MainLayout 등에서 사용하세요
 */
export const ErrorBoundaryReset = ({ children }) => {
  const location = useLocation();
  const errorBoundaryRef = useRef(null);

  useEffect(() => {
    // 라우트가 변경될 때마다 에러 상태 리셋
    if (errorBoundaryRef.current?.resetError) {
      errorBoundaryRef.current.resetError();
    }
  }, [location.pathname]);

  return <ErrorBoundary ref={errorBoundaryRef}>{children}</ErrorBoundary>;
};
